import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase.js';
import { doc, setDoc, updateDoc, arrayRemove, arrayUnion, collection, onSnapshot } from 'firebase/firestore';

/**
 * Custom hook for managing user-defined movie categories
 * Handles category CRUD operations and Firebase synchronization
 *
 * @param {Object} currentUser - Firebase authenticated user
 * @param {Function} showSnackbar - Function to show notifications
 * @returns {Object} Category state and methods
 */
const useCategories = (currentUser, showSnackbar) => {
  // Category state
  const [categories, setCategories] = useState([
    { id: 'all', name: 'All Movies' },
  ]);
  const [activeCategory, setActiveCategory] = useState('all');

  // Modal state
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddToCategoryModal, setShowAddToCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryErrorMessage, setCategoryErrorMessage] = useState('');
  const [selectedMovieForCategory, setSelectedMovieForCategory] = useState(null);

  /**
   * Open the Add Category Modal
   */
  const openAddCategoryModal = useCallback(() => {
    setNewCategoryName('');
    setCategoryErrorMessage('');
    setShowAddCategoryModal(true);
  }, []);

  /**
   * Close the Add Category Modal
   */
  const closeAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal(false);
  }, []);

  /**
   * Open the Add to Category Modal for a specific movie
   * @param {Object} movie - Movie object to add to categories
   */
  const openAddToCategoryModal = useCallback((movie) => {
    setSelectedMovieForCategory(movie);
    setShowAddToCategoryModal(true);
  }, []);

  /**
   * Close the Add to Category Modal
   */
  const closeAddToCategoryModal = useCallback(() => {
    setSelectedMovieForCategory(null);
    setShowAddToCategoryModal(false);
  }, []);

  /**
   * Create a new category
   */
  const createCategory = useCallback(async () => {
    if (!currentUser) {
      showSnackbar('Please log in to create categories.', 'warning');
      return;
    }

    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      setCategoryErrorMessage('Category name cannot be empty.');
      return;
    }

    if (categories.some((cat) => cat.name.toLowerCase() === trimmedName.toLowerCase())) {
      setCategoryErrorMessage('Category with this name already exists.');
      return;
    }

    try {
      const newCat = {
        id: trimmedName.toLowerCase().replace(/\s+/g, '-'),
        name: trimmedName,
      };

      const categoryRef = doc(
        db,
        'users',
        currentUser.uid,
        'categories',
        newCat.id
      );
      await setDoc(categoryRef, newCat);

      // If we're in the Manage Categories modal, automatically add the movie to the new category
      if (selectedMovieForCategory) {
        const movieRef = doc(
          db,
          'users',
          currentUser.uid,
          'library',
          selectedMovieForCategory.id
        );
        await updateDoc(movieRef, {
          categories: arrayUnion(newCat.id),
        });

        // Update local state
        setSelectedMovieForCategory({
          ...selectedMovieForCategory,
          categories: [...selectedMovieForCategory.categories, newCat.id],
        });

        showSnackbar(
          `Category "${trimmedName}" created and "${selectedMovieForCategory.title}" added to it!`,
          'success'
        );
      } else {
        showSnackbar(`Category "${trimmedName}" created!`, 'success');
      }

      setNewCategoryName('');
      setCategoryErrorMessage('');
      setShowAddCategoryModal(false);
    } catch (error) {
      console.error('Error creating category:', error);
      setCategoryErrorMessage('Failed to create category. Please try again.');
    }
  }, [currentUser, newCategoryName, categories, selectedMovieForCategory, showSnackbar]);

  /**
   * Toggle a movie's membership in a category (add or remove)
   * @param {string} categoryId - Category ID to toggle
   */
  const toggleMovieCategory = useCallback(async (categoryId) => {
    if (!selectedMovieForCategory || !currentUser) {
      return;
    }

    // Ensure the movie has a categories array
    if (!selectedMovieForCategory.categories) {
      setSelectedMovieForCategory({
        ...selectedMovieForCategory,
        categories: ['all'],
      });
      return;
    }

    try {
      const movieRef = doc(
        db,
        'users',
        currentUser.uid,
        'library',
        selectedMovieForCategory.id
      );

      // Determine if we're adding or removing the category
      const isAdding = !selectedMovieForCategory.categories.includes(categoryId);

      await updateDoc(movieRef, {
        categories: isAdding ? arrayUnion(categoryId) : arrayRemove(categoryId),
      });

      // Update local state immediately for better UX
      const updatedCategories = isAdding
        ? [...selectedMovieForCategory.categories, categoryId]
        : selectedMovieForCategory.categories.filter((cat) => cat !== categoryId);

      setSelectedMovieForCategory({
        ...selectedMovieForCategory,
        categories: updatedCategories,
      });

      // Show success message
      const categoryName = categories.find((cat) => cat.id === categoryId)?.name || categoryId;
      showSnackbar(
        isAdding
          ? `${selectedMovieForCategory.title} added to "${categoryName}"`
          : `${selectedMovieForCategory.title} removed from "${categoryName}"`,
        'success'
      );
    } catch (error) {
      console.error('Error updating movie categories:', error);
      showSnackbar('Failed to update categories. Please try again.', 'error');
    }
  }, [selectedMovieForCategory, currentUser, categories, showSnackbar]);

  /**
   * Delete a category
   * @param {string} categoryId - Category ID to delete
   */
  const deleteCategory = useCallback(async (categoryId) => {
    if (!currentUser || categoryId === 'all') {
      showSnackbar('Cannot delete the "All Movies" category.', 'warning');
      return;
    }

    try {
      const categoryRef = doc(
        db,
        'users',
        currentUser.uid,
        'categories',
        categoryId
      );

      // Note: This would need to also remove the category from all movies
      // that have it. This is a simplified version.
      await categoryRef.delete();

      const categoryName = categories.find((cat) => cat.id === categoryId)?.name;
      showSnackbar(`Category "${categoryName}" deleted!`, 'success');

      // If the deleted category was active, switch to "all"
      if (activeCategory === categoryId) {
        setActiveCategory('all');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showSnackbar('Failed to delete category. Please try again.', 'error');
    }
  }, [currentUser, categories, activeCategory, showSnackbar]);

  /**
   * Handle category name input change
   * @param {Event} e - Input change event
   */
  const handleCategoryNameChange = useCallback((e) => {
    setNewCategoryName(e.target.value);
    setCategoryErrorMessage('');
  }, []);

  /**
   * Handle active category change
   * @param {string} categoryId - Category ID to set as active
   */
  const handleActiveCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);
  }, []);

  // Fetch categories from Firestore when user logs in
  useEffect(() => {
    if (!currentUser) {
      setCategories([{ id: 'all', name: 'All Movies' }]);
      setActiveCategory('all');
      return;
    }

    const categoriesRef = collection(
      db,
      'users',
      currentUser.uid,
      'categories'
    );

    const unsubscribeCategories = onSnapshot(
      categoriesRef,
      (snapshot) => {
        const userCategories = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setCategories([{ id: 'all', name: 'All Movies' }, ...userCategories]);
      },
      (error) => {
        console.error('Error in categories listener:', error);
        showSnackbar('Failed to load categories.', 'error');
      }
    );

    return () => {
      unsubscribeCategories();
    };
  }, [currentUser, showSnackbar]);

  return {
    // State
    categories,
    activeCategory,
    showAddCategoryModal,
    showAddToCategoryModal,
    newCategoryName,
    categoryErrorMessage,
    selectedMovieForCategory,

    // Actions
    openAddCategoryModal,
    closeAddCategoryModal,
    openAddToCategoryModal,
    closeAddToCategoryModal,
    createCategory,
    toggleMovieCategory,
    deleteCategory,
    handleCategoryNameChange,
    handleActiveCategoryChange,
    setActiveCategory,
    setNewCategoryName, // Expose setter for direct input control
  };
};

export default useCategories;
